import React from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Users, Plus, Trash2, Send, UserPlus, FolderPlus } from 'lucide-react';

const GroupsTab = ({
  groups,
  setShowCreateGroup,
  deleteGroup,
  openInviteDialog,
  navigate
}) => {
  return (
    <Card className="bg-white border-gray-200 shadow-sm">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-xl text-gray-900">Talent Groups</CardTitle>
          <Button
            onClick={() => setShowCreateGroup(true)}
            className="bg-[#03DFFC] hover:bg-[#03DFFC]/90 text-gray-900"
            data-testid="create-group-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Group
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {groups.length === 0 ? (
          <div className="text-center py-10 px-4" data-testid="groups-empty">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[#03DFFC]/10 flex items-center justify-center">
              <Users className="w-7 h-7 text-[#03DFFC]" />
            </div>
            <p className="text-sm font-semibold text-gray-900 mb-1">No talent groups yet</p>
            <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">
              Organize your favorite talent into reusable groups — perfect for repeat events, regional crews, or VIP rosters.
            </p>
            <Button
              onClick={() => setShowCreateGroup(true)}
              className="bg-[#03DFFC] hover:bg-[#03DFFC]/90 text-gray-900"
              data-testid="groups-create-cta"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create a Group
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {groups.map((group) => (
              <Card 
                key={group.id} 
                className="bg-gray-50 border-gray-200 hover:border-[#03DFFC] transition-colors cursor-pointer"
                data-testid={`group-card-${group.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#03DFFC]" />
                        {group.name}
                      </h3>
                      {group.description && (
                        <p className="text-sm text-gray-500 mt-1">{group.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {group.member_count || 0} members
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); openInviteDialog('group', group.id); }}
                        className="h-8 w-8 p-0 text-[#FF2D95] hover:bg-[#FF2D95]/10"
                        title="Invite group to job"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); deleteGroup(group.id); }}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-500"
                        title="Delete group"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {/* Group members preview */}
                  {group.members && group.members.length > 0 && (
                    <div className="mt-3 flex -space-x-2">
                      {group.members.slice(0, 5).map((member, i) => (
                        member.profile_photo_url ? (
                          <img 
                            key={i}
                            src={member.profile_photo_url} 
                            alt=""
                            className="w-8 h-8 rounded-full border-2 border-white object-cover"
                          />
                        ) : (
                          <div 
                            key={i}
                            className="w-8 h-8 rounded-full border-2 border-white bg-[#03DFFC]/10 flex items-center justify-center text-[10px] text-[#03DFFC]"
                          >
                            {member.first_name?.[0]}{member.last_name?.[0]}
                          </div>
                        )
                      ))}
                      {group.member_count > 5 && (
                        <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs text-gray-600">
                          +{group.member_count - 5}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GroupsTab;
